import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function NewProjectDialog({ onCreated, trigger }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        name: "",
        address: "",
        description: "",
        budget_estimate: "",
    });

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            toast.error("Le nom du chantier est obligatoire");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                address: form.address || null,
                description: form.description || null,
                budget_estimate: form.budget_estimate
                    ? parseFloat(form.budget_estimate)
                    : null,
            };
            const created = await api.createProject(payload);
            toast.success("Chantier créé");
            setOpen(false);
            setForm({ name: "", address: "", description: "", budget_estimate: "" });
            onCreated && onCreated(created);
        } catch (e) {
            toast.error("Erreur lors de la création");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button
                        data-testid="new-project-btn"
                        className="rounded-none bg-ink text-white hover:bg-ink/90"
                    >
                        <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
                        Nouveau chantier
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="rounded-none border-ink max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        Nouveau chantier
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="stat-label">Nom du chantier *</Label>
                        <Input
                            data-testid="new-project-name"
                            className="rounded-none mt-1.5"
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            placeholder="Rénovation R+2 Rue Vavin"
                        />
                    </div>
                    <div>
                        <Label className="stat-label">Adresse</Label>
                        <Input
                            data-testid="new-project-address"
                            className="rounded-none mt-1.5"
                            value={form.address}
                            onChange={(e) =>
                                setForm({ ...form, address: e.target.value })
                            }
                            placeholder="12 Rue Vavin, 75006 Paris"
                        />
                    </div>
                    <div>
                        <Label className="stat-label">
                            Budget estimé HT (€)
                        </Label>
                        <Input
                            data-testid="new-project-budget"
                            type="number"
                            className="rounded-none mt-1.5 num"
                            value={form.budget_estimate}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    budget_estimate: e.target.value,
                                })
                            }
                            placeholder="150000"
                        />
                    </div>
                    <div>
                        <Label className="stat-label">Description</Label>
                        <Textarea
                            data-testid="new-project-description"
                            className="rounded-none mt-1.5"
                            rows={3}
                            value={form.description}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    description: e.target.value,
                                })
                            }
                            placeholder="Rénovation complète d'un appartement de 110 m² avec création SDB et cuisine ouverte."
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setOpen(false)}
                    >
                        Annuler
                    </Button>
                    <Button
                        data-testid="new-project-submit"
                        className="rounded-none bg-ink text-white hover:bg-ink/90"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Création..." : "Créer le chantier"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
